import { featureFlags } from '@seen/core/featureFlags'
import {
  formatMedicationDosageLine,
  formatMedicationInstructionLines,
  formatMedicationRefillLine,
  isMedicationRefillActionRequired,
} from '@seen/core/models/medicationHoverCard'
import {
  type ExtendedMedicationRequest,
  ExtendedMedicationRequest as MedReqClass,
} from '@seen/core/models/medicationRequest'
import { Button, cn, Separator, TypographySubtle } from '@seen/ui'
import {
  IconAlertTriangle,
  IconClockExclamation,
  IconFileText,
  IconInfoCircle,
  IconPill,
  IconReload,
  IconSignature,
} from '@tabler/icons-react'
import { orderBy } from 'lodash-es'
import type { JSX, ReactNode } from 'react'
import { MedicationActivity } from '#src/components/medications/MedicationActivity.tsx'
import {
  MedicationStateBadge,
  MedicationTypeBadge,
} from '#src/components/medications/MedicationBadges.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

interface MedicationHistoryTooltipProps {
  medicationDisplay: ReactNode
  medicationRequests: ExtendedMedicationRequest[]
  onDraftPrescription?: (
    medication: ExtendedMedicationRequest,
    orderType: 'Refill'
  ) => void
  onOpenDraftPrescription?: (
    draftId: number | string,
    medication: ExtendedMedicationRequest
  ) => void
  draftId?: number | string
  isCreatingDraft?: boolean
  highlightedRequestIds?: ReadonlySet<string>
  // The request the card describes, badges, and acts on. A matching id pins
  // that request. Otherwise the card uses the representative, then the newest.
  actionRequestId?: string | null
}

export function MedicationHistoryTooltip({
  medicationDisplay,
  medicationRequests,
  onDraftPrescription,
  onOpenDraftPrescription,
  draftId,
  isCreatingDraft,
  highlightedRequestIds,
  actionRequestId,
}: MedicationHistoryTooltipProps): JSX.Element {
  const sortedRequests = orderBy(
    medicationRequests,
    (request) => request.authoredOn ?? '',
    ['desc']
  )

  const mostRecent = sortedRequests[0]
  // Same request the row and the status badge show, so Refill never drafts a
  // newer stopped or one-time order from the thread, and a later one-time
  // clinic dispense does not replace the displayed sig.
  const actionRequest =
    (actionRequestId == null
      ? undefined
      : medicationRequests.find((request) => request.id === actionRequestId)) ??
    MedReqClass.getRepresentative(medicationRequests) ??
    mostRecent
  const isPinned =
    actionRequestId != null && actionRequest?.id === actionRequestId
  const refillLine = actionRequest
    ? formatMedicationRefillLine(actionRequest)
    : null
  const refillReason = actionRequest?.refillUnavailableReason ?? undefined
  const showSignAction =
    onOpenDraftPrescription !== undefined &&
    draftId !== undefined &&
    actionRequest !== undefined
  const showRefillAction =
    !showSignAction &&
    onDraftPrescription !== undefined &&
    !!actionRequest?.externalResourceJSON
  const canDraft = showRefillAction && !refillReason

  const rxHoverCard = useFeatureFlag(featureFlags.facesheetRxHoverCard)

  const rawRequests = medicationRequests.map((mr) => mr.toFhirResource())
  const stateRequests =
    isPinned && actionRequest ? [actionRequest.toFhirResource()] : rawRequests
  const medicationState = MedReqClass.classifyMedicationState(stateRequests)
  const issues = MedReqClass.detectIssues(stateRequests)

  const actions = (showSignAction || showRefillAction) && (
    <HoverCardActions
      actionRequest={actionRequest}
      showSignAction={showSignAction}
      showRefillAction={showRefillAction}
      canDraft={canDraft}
      isCreatingDraft={isCreatingDraft}
      draftId={draftId}
      refillReason={refillReason}
      onDraftPrescription={onDraftPrescription}
      onOpenDraftPrescription={onOpenDraftPrescription}
    />
  )

  return (
    // No min-width: it would override the hover card's max-width cap in a
    // narrow window. HoverCardContent is shrink-to-fit, so the content settles
    // at its natural width and the cap clamps it.
    <div className="flex max-w-xl flex-col gap-3 px-1 py-2.5">
      {rxHoverCard ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="text-sm font-semibold tracking-tight">
                {medicationDisplay}
              </span>
              {refillLine && (
                <span
                  className={cn(
                    'text-xs',
                    actionRequest &&
                      isMedicationRefillActionRequired(actionRequest)
                      ? 'text-orange-500'
                      : 'text-muted-foreground'
                  )}
                >
                  {refillLine}
                </span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <MedicationStateBadge state={medicationState} />
              <MedicationTypeBadge type={actionRequest?.type} />
            </div>
          </div>
          {actions}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">{medicationDisplay}</span>
          <MedicationStateBadge state={medicationState} />
          <MedicationTypeBadge type={actionRequest?.type} />
        </div>
      )}

      {(medicationState.category === 'stopped' ||
        medicationState.category === 'cancelled' ||
        medicationState.category === 'completed') && (
        <TypographySubtle className="text-xs">
          Reason: {medicationState.description}
        </TypographySubtle>
      )}

      {!rxHoverCard && actions && (
        <>
          <Separator />
          {actions}
        </>
      )}

      {actionRequest &&
        (actionRequest.dispenseRequest?.quantity ||
          actionRequest.dosageInstructions) && (
          <>
            <Separator />
            <DosageInfo
              medicationRequest={actionRequest}
              standardized={rxHoverCard}
            />
          </>
        )}

      {/* Issues */}
      {issues.length > 0 && (
        <>
          <Separator />
          <div className="flex flex-col gap-1">
            <div className="text-xs font-medium">Issues</div>
            {issues.map((issue, idx) => (
              <IssueItem key={`${issue.type}-${idx}`} issue={issue} />
            ))}
          </div>
        </>
      )}

      {/* Event History - comprehensive timeline across all medication versions */}
      {mostRecent && sortedRequests.length > 0 && (
        <>
          <Separator />
          <div className="flex flex-col gap-1.5">
            <div className="text-xs font-medium">History</div>
            <MedicationActivity
              medicationRequest={mostRecent}
              groupedMedications={sortedRequests}
              highlightedRequestIds={highlightedRequestIds}
              collapsible={rxHoverCard}
            />
          </div>
        </>
      )}
    </div>
  )
}

function HoverCardActions({
  actionRequest,
  showSignAction,
  showRefillAction,
  canDraft,
  isCreatingDraft,
  draftId,
  refillReason,
  onDraftPrescription,
  onOpenDraftPrescription,
}: {
  actionRequest: ExtendedMedicationRequest | undefined
  showSignAction: boolean
  showRefillAction: boolean
  canDraft: boolean
  isCreatingDraft?: boolean
  draftId?: number | string
  refillReason?: string
  onDraftPrescription?: MedicationHistoryTooltipProps['onDraftPrescription']
  onOpenDraftPrescription?: MedicationHistoryTooltipProps['onOpenDraftPrescription']
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      {showSignAction && actionRequest && draftId !== undefined && (
        <Button
          size="xs"
          variant="outline"
          onClick={() => onOpenDraftPrescription?.(draftId, actionRequest)}
          className="w-fit justify-start"
        >
          <IconSignature className="size-3.5" />
          Sign
        </Button>
      )}
      {showRefillAction && actionRequest && (
        <>
          <Button
            size="xs"
            variant="outline"
            className="w-fit justify-start"
            disabled={!canDraft || isCreatingDraft}
            loading={isCreatingDraft}
            onClick={() => {
              if (canDraft) {
                onDraftPrescription?.(actionRequest, 'Refill')
              }
            }}
          >
            <IconReload className="size-3.5" />
            Refill
          </Button>
          {refillReason && (
            <TypographySubtle className="text-[10px]">
              {refillReason}
            </TypographySubtle>
          )}
        </>
      )}
    </div>
  )
}

function IssueItem({
  issue,
}: {
  issue: ReturnType<typeof MedReqClass.detectIssues>[number]
}): JSX.Element {
  const severityIconMap: Record<typeof issue.severity, JSX.Element> = {
    error: <IconAlertTriangle className="size-3 text-red-500" />,
    warning: <IconClockExclamation className="size-3 text-orange-500" />,
    info: <IconInfoCircle className="size-3 text-blue-500" />,
  }

  return (
    <div className="flex items-start gap-1.5 text-xs">
      {severityIconMap[issue.severity]}
      <span className="text-muted-foreground">{issue.message}</span>
    </div>
  )
}

function IconFileLinesCheck({
  className,
}: {
  className?: string
}): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      focusable="false"
      role="img"
      viewBox="0 0 8.5 9.5"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.8075 8.5H1.5C1.23478 8.5 0.98043 8.39464 0.792893 8.20711C0.605357 8.01957 0.5 7.76522 0.5 7.5V1.5C0.5 1.23478 0.605357 0.98043 0.792893 0.792893C0.98043 0.605357 1.23478 0.5 1.5 0.5H5.5C5.76522 0.5 6.01957 0.605357 6.20711 0.792893C6.39464 0.98043 6.5 1.23478 6.5 1.5V5.5M5 8L6 9L8 7M2.5 2.5H4.5M2.5 4.5H3.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function DosageInfo({
  medicationRequest,
  standardized,
}: {
  medicationRequest: ExtendedMedicationRequest
  standardized: boolean
}): JSX.Element {
  if (standardized) {
    const dosageLine = formatMedicationDosageLine(medicationRequest)
    const instructionLines = formatMedicationInstructionLines(medicationRequest)
    const supplyDuration =
      medicationRequest.dispenseRequest?.expectedSupplyDuration
    return (
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold">Dosage Details</div>
        {(dosageLine || supplyDuration) && (
          <div className="flex items-start gap-1 text-xs">
            <IconPill className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
            <div className="flex flex-col">
              {dosageLine && (
                <span className="text-muted-foreground">{dosageLine}</span>
              )}
              {supplyDuration && (
                <TypographySubtle className="text-[10px]">
                  {supplyDuration.value}-day supply
                </TypographySubtle>
              )}
            </div>
          </div>
        )}
        {instructionLines.length > 0 && (
          <div className="flex items-start gap-1 text-xs">
            <IconFileLinesCheck className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
            <div className="flex flex-col">
              {instructionLines.map((line, idx) => (
                <span key={`${line}-${idx}`} className="text-muted-foreground">
                  {line}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const quantity = medicationRequest.dispenseRequest?.quantity
  const dosageInstructions = medicationRequest.dosageInstructions
  const supplyDuration =
    medicationRequest.dispenseRequest?.expectedSupplyDuration

  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-xs font-medium">Dosage Details</div>

      {quantity && (
        <div className="flex items-start gap-1.5 text-xs">
          <IconPill className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
          <div className="flex flex-col gap-0.5">
            <span className="text-foreground">
              Quantity: {quantity.value} {quantity.unit || 'units'}
            </span>
            {supplyDuration && (
              <TypographySubtle className="text-[10px]">
                {supplyDuration.value}-day supply
              </TypographySubtle>
            )}
          </div>
        </div>
      )}

      {dosageInstructions && dosageInstructions.length > 0 && (
        <div className="flex items-start gap-1.5 text-xs">
          <IconFileText className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
          <div className="flex flex-col gap-0.5">
            {dosageInstructions.map((instruction, idx) => (
              <span
                key={`${instruction}-${idx}`}
                className="text-muted-foreground"
              >
                {instruction}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
